import React, { useRef, useEffect, forwardRef } from "react"
import Tag from "./components/Tag"
import DropdownMenu from "./components/DropdownMenu"
import { useMultiSelect } from "./hooks/useMultiSelect"
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation"
import { MultiSelectProps } from "./types"

const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      value = [],
      onChange,
      placeholder = "選択してください",
      disabled = false,
      hasError = false,
      className = "",
      maxItems,
      searchable = true,
      clearable = true,
      closeOnSelect = false,
      ...props
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const {
      selectedValues,
      selectedOptions,
      isOpen,
      searchValue,
      focusedIndex,
      filteredOptions,
      isMaxReached,
      toggleOption,
      removeValue,
      clearAll,
      updateSearchValue,
      openDropdown,
      closeDropdown,
      toggleDropdown,
      selectFocusedOption,
      setFocusedIndex,
    } = useMultiSelect({
      options,
      value,
      onChange,
      maxItems,
      searchable,
      closeOnSelect,
    })

    // 最後の値を削除（Backspaceキー用）
    const removeLastValue = React.useCallback(() => {
      if (selectedValues.length > 0) {
        const lastValue = selectedValues[selectedValues.length - 1]
        removeValue(lastValue)
      }
    }, [selectedValues, removeValue])

    const { handleKeyDown } = useKeyboardNavigation({
      isOpen,
      filteredOptions,
      focusedIndex,
      setFocusedIndex,
      selectFocusedOption,
      closeDropdown,
      removeLastValue,
      searchValue,
    })

    // 外部クリックでドロップダウンを閉じる
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          closeDropdown()
        }
      }

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
      }
    }, [isOpen, closeDropdown])

    // キーボードイベントリスナー
    useEffect(() => {
      const handleKeyDownEvent = (e: KeyboardEvent) => {
        handleKeyDown(e)
      }

      if (containerRef.current) {
        const container = containerRef.current
        container.addEventListener("keydown", handleKeyDownEvent)
        return () => container.removeEventListener("keydown", handleKeyDownEvent)
      }
    }, [handleKeyDown])

    // スタイルクラス
    const baseClasses = `relative w-full border border-medium-light-gray rounded-md bg-white font-noto font-normal text-[1.4rem] leading-[1.5] text-smooth-black focus-within:border-smooth-blue`
    const borderClasses = hasError ? "border-error" : "border-smooth-gray"
    const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"

    const combinedClasses = `${baseClasses} ${borderClasses} ${disabledClasses} ${className}`.trim()

    const handleContainerClick = () => {
      if (!disabled) {
        if (searchable && searchInputRef.current) {
          searchInputRef.current.focus()
        }
        openDropdown()
      }
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSearchValue(e.target.value)
    }

    const handleSearchFocus = () => {
      if (!disabled) {
        openDropdown()
      }
    }

    const showPlaceholder = selectedOptions.length === 0 && !searchValue

    return (
      <div ref={ref} className={combinedClasses} {...props}>
        <div
          ref={containerRef}
          className="relative"
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-disabled={disabled}
        >
          {/* メインの入力エリア */}
          <div
            className="min-h-[48px] py-[15px] px-3 flex flex-wrap gap-1 items-center"
            onClick={handleContainerClick}
          >
            {/* 選択されたタグ */}
            {selectedOptions.map((option) => (
              <Tag
                key={option.value}
                label={option.label}
                onRemove={() => removeValue(option.value)}
                disabled={disabled}
              />
            ))}

            {/* 検索入力 / プレースホルダー */}
            <div className="flex-1 min-w-[120px]">
              {searchable ? (
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  placeholder={showPlaceholder ? placeholder : ""}
                  disabled={disabled}
                  className="w-full bg-transparent border-none outline-none text-[1.4rem] placeholder:text-smooth-black placeholder:opacity-60"
                />
              ) : (
                showPlaceholder && (
                  <span className="text-smooth-black opacity-60">{placeholder}</span>
                )
              )}
            </div>

            {/* 右側のボタンエリア */}
            <div className="flex items-center ml-2">
              {/* クリアボタン */}
              {clearable && selectedValues.length > 0 && !disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    clearAll()
                  }}
                  className="text-smooth-black hover:text-smooth-blue opacity-60 hover:opacity-100 transition-all duration-150 p-1"
                  aria-label="すべてクリア"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}

              {/* ドロップダウン矢印 */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleDropdown()
                }}
                disabled={disabled}
                className="text-smooth-black opacity-60 p-1"
                aria-label={isOpen ? "ドロップダウンを閉じる" : "ドロップダウンを開く"}
              >
                <svg
                  className={`w-4 h-4 transition-transform duration-150 ${
                    isOpen ? "transform rotate-180" : ""
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* 最大選択数に達した場合の表示 */}
          {isMaxReached && (
            <div className="px-3 pb-2 text-xs text-smooth-black opacity-60">
              最大{maxItems}件まで選択できます
            </div>
          )}

          {/* ドロップダウンメニュー */}
          <DropdownMenu
            options={filteredOptions}
            selectedValues={selectedValues}
            onSelect={toggleOption}
            searchValue={searchValue}
            focusedIndex={focusedIndex}
            isOpen={isOpen}
          />
        </div>
      </div>
    )
  }
)

MultiSelect.displayName = "MultiSelect"

export default MultiSelect
